import { computed, reactive, ref, type ComputedRef, type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  type ContractManagementDateType,
  type ContractManagementCalendarCreate,
  type ContractManagementCalendarInDb,
  type ContractManagementCalendarLinkResult,
  type ContractManagementCalendarUpdate,
  type UserInDb,
} from '@/api';
import type { VaultPickerCriteria } from '@/components/design/domain/fileVault/vaultPicker';
import { CalendarService } from '@/services/calendarService';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';

export type CalendarWizardStepKey = 'setup' | 'dates' | 'sources';

export interface CalendarWizardStepDefinition {
  key: CalendarWizardStepKey;
  number: number;
  /** Rail label. */
  title: string;
  /** One line under the rail entry and under the panel heading. */
  hint: string;
  /** Panel heading for the step's body. */
  heading: string;
}

export const CALENDAR_WIZARD_STEPS: CalendarWizardStepDefinition[] = [
  {
    key: 'setup',
    number: 1,
    title: 'Setup',
    hint: 'Name, who receives the emails and when to remind.',
    heading: 'Set up the calendar',
  },
  {
    key: 'dates',
    number: 2,
    title: 'Dates',
    hint: 'Contract dates and/or a prompt that extracts new ones.',
    heading: 'Choose the dates',
  },
  {
    key: 'sources',
    number: 3,
    title: 'Documents',
    hint: 'Pick folders and contracts, or keep a contract search.',
    heading: 'Choose what the calendar sees',
  },
];

/**
 * The starter prompt behind "Start from the termination-dates prompt".
 *
 * Long on purpose: it is the worked example of what a calendar prompt has to
 * spell out — which dates, how to handle a period stated relative to another
 * date, and that returning nothing beats guessing.
 */
export const TERMINATION_DATES_PROMPT = [
  'Find all termination-related dates in this contract.',
  "I want to know when this agreement ends and any dates that matter for ending or renewing it, so I don't miss a deadline. Specifically, extract:",
  '- The termination / expiry date — the date the contract ends or is set to end.\n- Any notice deadline for terminating or cancelling — i.e. the last date by which I have to give notice to end the contract (for example, "either party may terminate with 3 months\' written notice before the end of the term" → give me the date that notice is actually due, not just the end date).\n- Any auto-renewal date — if the contract renews automatically unless cancelled, the date that renewal kicks in.\n- The end of any minimum term or lock-in period, if it differs from the overall termination date.',
  "If the contract runs for a fixed term and then renews on a repeating cycle (e.g. renews every year, or every 12 months), capture that as a recurring date so I'm reminded each cycle, with the correct interval and frequency. If it's a one-off date that doesn't repeat, just give me that single date.",
  'Calculate the actual calendar dates where a clause describes a period relative to another date (e.g. "90 days before expiry", "6 weeks prior to the renewal date"). Base any relative calculation on the dates stated in the document.',
  "Only return dates that are actually supported by the contract text — if the contract doesn't mention termination or a notice deadline, return nothing rather than guessing. For each date, the date itself is what matters most.",
].join('\n\n');

export type CalendarSourceMode = 'pick' | 'search';

/**
 * The contract search a calendar keeps: every contract its creator can read
 * that matches is in the calendar, now and later. Empty text and no filters
 * match all of them.
 */
export type CalendarContractCriteria = VaultPickerCriteria;

/**
 * Everything the three steps edit. `receiverEmails` are free-text addresses on
 * the calendar payload; `receiverUids` are organisation users linked through a
 * separate endpoint. They are genuinely two different things — an address that
 * belongs to nobody in the workspace still gets the digest.
 */
export interface CalendarWizardForm {
  name: string;
  receiverEmails: string[];
  /** Fixed dates already stored per contract file (effective from / to). */
  dateTypes: ContractManagementDateType[];
  /** Whether the calendar also runs its own prompt, which extracts new dates. */
  useCustomPrompt: boolean;
  prompt: string;
  exampleData: string[];
  firstReminderWeeks: number | null;
  secondReminderWeeks: number | null;
  receiverUids: string[];
  /**
   * What the calendar sees: picked folders and contracts, or every contract
   * matching a search. Only the active one is saved; the other is cleared.
   */
  sourceMode: CalendarSourceMode;
  /** Vault folders the calendar reads, including whatever is added to them later. */
  sourceDirectoryUids: string[];
  /** Contracts the calendar reads, each on its own. */
  sourceContractUids: string[];
  contractCriteria: CalendarContractCriteria;
}

/** The link collections, as they stood when the wizard loaded them. */
interface CalendarLinkSnapshot {
  receiverUids: string[];
  sourceDirectoryUids: string[];
  sourceContractUids: string[];
}

/**
 * Records already linked, kept so the pickers can label a uid the remote
 * search has not returned yet.
 */
export interface CalendarWizardSeeds {
  receivers: UserInDb[];
}

export interface UseCalendarWizardReturn {
  form: CalendarWizardForm;
  seeds: CalendarWizardSeeds;
  activeStep: Ref<CalendarWizardStepKey>;
  activeStepDefinition: ComputedRef<CalendarWizardStepDefinition>;
  isEditMode: ComputedRef<boolean>;
  title: ComputedRef<string>;
  loading: Ref<boolean>;
  loadError: Ref<string | null>;
  saving: Ref<boolean>;
  saveError: Ref<string | null>;
  /** What the active step is still missing, as a sentence. `null` when ready. */
  activeStepBlocker: ComputedRef<string | null>;
  continueDisabled: ComputedRef<boolean>;
  canGoBack: ComputedRef<boolean>;
  continueLabel: ComputedRef<string>;
  footerNote: ComputedRef<string>;
  isStepComplete: (stepKey: CalendarWizardStepKey) => boolean;
  isStepAccessible: (stepKey: CalendarWizardStepKey) => boolean;
  selectStep: (stepKey: CalendarWizardStepKey) => void;
  goBack: () => void;
  goNext: () => Promise<void>;
  updateForm: (patch: Partial<CalendarWizardForm>) => void;
  hydrate: () => Promise<void>;
  navigateBack: () => void;
}

function emptyForm(): CalendarWizardForm {
  return {
    name: '',
    receiverEmails: [],
    dateTypes: [],
    useCustomPrompt: false,
    prompt: '',
    exampleData: [],
    firstReminderWeeks: null,
    secondReminderWeeks: null,
    receiverUids: [],
    sourceMode: 'pick',
    sourceDirectoryUids: [],
    sourceContractUids: [],
    contractCriteria: { text: '', types: [], tags: [] },
  };
}

/** uids in `next` that were not in `previous`, and vice versa. */
function diffUids(previous: string[], next: string[]): { added: string[]; removed: string[] } {
  return {
    added: next.filter(uid => !previous.includes(uid)),
    removed: previous.filter(uid => !next.includes(uid)),
  };
}

export function useCalendarWizard(): UseCalendarWizardReturn {
  const route = useRoute();
  const router = useRouter();
  const message = useGaiaToast();

  const calendarUid = computed(() => {
    const raw = route.params.uid;
    return typeof raw === 'string' && raw.length > 0 ? raw : null;
  });
  const isEditMode = computed(() => calendarUid.value !== null);

  const form = reactive<CalendarWizardForm>(emptyForm());
  const seeds = reactive<CalendarWizardSeeds>({
    receivers: [],
  });

  // What the links looked like on load. Create mode leaves it empty, so the
  // save diff reduces to "add everything picked" without a second code path.
  const pristineLinks = ref<CalendarLinkSnapshot>({
    receiverUids: [],
    sourceDirectoryUids: [],
    sourceContractUids: [],
  });

  const activeStep = ref<CalendarWizardStepKey>('setup');
  const loading = ref<boolean>(false);
  const loadError = ref<string | null>(null);
  const saving = ref<boolean>(false);
  const saveError = ref<string | null>(null);

  const currentStepIndex = computed(() =>
    CALENDAR_WIZARD_STEPS.findIndex(step => step.key === activeStep.value)
  );
  const activeStepDefinition = computed(
    () => CALENDAR_WIZARD_STEPS[currentStepIndex.value] ?? CALENDAR_WIZARD_STEPS[0]
  );
  const title = computed(() => (isEditMode.value ? 'Edit calendar' : 'Create calendar'));

  const hasName = computed(() => form.name.trim().length > 0);
  const hasPrompt = computed(() => form.prompt.trim().length > 0);
  const isSetupComplete = computed(() => hasName.value);
  // A calendar needs at least one source of dates: a fixed date type, or its
  // own prompt. Both together are fine.
  const hasDateSource = computed(
    () => form.dateTypes.length > 0 || (form.useCustomPrompt && hasPrompt.value)
  );
  const hasPickedSource = computed(
    () => form.sourceDirectoryUids.length > 0 || form.sourceContractUids.length > 0
  );
  // A search always sees something — an empty one sees every contract — but
  // picking nothing would leave the calendar empty.
  const hasSource = computed(() => form.sourceMode === 'search' || hasPickedSource.value);

  // ── Step gating ───────────────────────────────────────────────────────────

  /**
   * Whether a step has everything the *next* step needs. Nothing unlocks until
   * setup's required fields are filled; the last step only gates saving.
   */
  function isStepSatisfied(stepKey: CalendarWizardStepKey): boolean {
    switch (stepKey) {
      case 'setup':
        return isSetupComplete.value;
      case 'dates':
        return hasDateSource.value;
      case 'sources':
        return hasSource.value;
    }
  }

  /** Whether the rail should tick the step off. */
  function isStepComplete(stepKey: CalendarWizardStepKey): boolean {
    switch (stepKey) {
      case 'setup':
        return isSetupComplete.value;
      case 'dates':
        return hasDateSource.value;
      case 'sources':
        return hasSource.value;
    }
  }

  function isStepAccessible(stepKey: CalendarWizardStepKey): boolean {
    const index = CALENDAR_WIZARD_STEPS.findIndex(step => step.key === stepKey);
    return CALENDAR_WIZARD_STEPS.slice(0, index).every(step => isStepSatisfied(step.key));
  }

  // The blocker and the disabled state read the same predicates, so the button
  // and the sentence explaining it can never disagree.
  const activeStepBlocker = computed<string | null>(() => {
    switch (activeStep.value) {
      case 'setup':
        if (!hasName.value) return 'Give the calendar a name before continuing';
        return null;
      case 'dates':
        // An open-but-empty prompt is its own miss, even with a contract date picked.
        if (form.useCustomPrompt && !hasPrompt.value) {
          return 'Describe the dates to extract before continuing';
        }
        if (!hasDateSource.value) {
          return 'Pick a contract date and/or add an extraction prompt before continuing';
        }
        return null;
      case 'sources':
        if (!hasSource.value) {
          return 'Pick a folder or contract before saving';
        }
        return null;
    }

    // Unreachable: the switch covers every step key.
    return null;
  });

  const continueDisabled = computed(() => saving.value || activeStepBlocker.value !== null);
  const canGoBack = computed(() => currentStepIndex.value > 0);
  const isLastStep = computed(() => currentStepIndex.value === CALENDAR_WIZARD_STEPS.length - 1);

  const continueLabel = computed(() => {
    if (!isLastStep.value) return 'Continue';
    return isEditMode.value ? 'Save changes' : 'Create calendar';
  });

  const footerNote = computed(() => {
    if (activeStepBlocker.value) return activeStepBlocker.value;
    if (isLastStep.value) return 'Everything here stays editable after saving.';
    const next = CALENDAR_WIZARD_STEPS.at(currentStepIndex.value + 1);
    return next
      ? `Step ${currentStepIndex.value + 1} of ${CALENDAR_WIZARD_STEPS.length} complete — next: ${next.title}`
      : '';
  });

  // ── Navigation ────────────────────────────────────────────────────────────

  function selectStep(stepKey: CalendarWizardStepKey): void {
    if (!isStepAccessible(stepKey)) return;
    activeStep.value = stepKey;
  }

  function goBack(): void {
    if (!canGoBack.value) return;
    activeStep.value = CALENDAR_WIZARD_STEPS[currentStepIndex.value - 1].key;
  }

  function navigateBack(): void {
    void router.push({ name: 'contract-management-calendar' });
  }

  function updateForm(patch: Partial<CalendarWizardForm>): void {
    Object.assign(form, patch);
  }

  // ── Hydration ─────────────────────────────────────────────────────────────

  function applyCalendar(calendar: ContractManagementCalendarInDb): void {
    form.name = calendar.name;
    form.receiverEmails = [...calendar.receiver_emails];
    form.dateTypes = [...(calendar.date_types ?? [])];
    form.prompt = calendar.prompt ?? '';
    form.useCustomPrompt = form.prompt.trim().length > 0;
    form.exampleData = [...(calendar.example_data ?? [])];
    form.firstReminderWeeks = calendar.first_reminder_weeks ?? null;
    form.secondReminderWeeks = calendar.second_reminder_weeks ?? null;
    const criteria = calendar.contract_criteria;
    form.sourceMode = criteria ? 'search' : 'pick';
    form.contractCriteria = {
      text: criteria?.text ?? '',
      types: [...(criteria?.column_filters?.type ?? [])],
      tags: [...(criteria?.column_filters?.tags ?? [])],
    };
  }

  async function hydrate(): Promise<void> {
    const uid = calendarUid.value;
    if (uid === null) return;

    loading.value = true;
    loadError.value = null;
    try {
      const [calendar, receivers, sourceDirectories, sourceContracts] = await Promise.all([
        CalendarService.getById(uid),
        CalendarService.listReceivers(uid),
        CalendarService.listSourceDirectories(uid),
        CalendarService.listSourceContracts(uid),
      ]);

      applyCalendar(calendar);
      seeds.receivers = receivers;

      const snapshot: CalendarLinkSnapshot = {
        receiverUids: receivers.map(user => user.uid),
        sourceDirectoryUids: sourceDirectories.map(directory => directory.uid),
        sourceContractUids: sourceContracts.map(contract => contract.uid),
      };
      pristineLinks.value = snapshot;
      form.receiverUids = [...snapshot.receiverUids];
      form.sourceDirectoryUids = [...snapshot.sourceDirectoryUids];
      form.sourceContractUids = [...snapshot.sourceContractUids];
    } catch (error) {
      logger.error('Error loading calendar for editing:', error);
      loadError.value = error instanceof Error ? error.message : 'Failed to load calendar';
    } finally {
      loading.value = false;
    }
  }

  // ── Saving ────────────────────────────────────────────────────────────────

  function buildPayload(): ContractManagementCalendarCreate & ContractManagementCalendarUpdate {
    const exampleData = form.exampleData.map(entry => entry.trim()).filter(Boolean);
    // Switching the prompt off clears it on the calendar, so no extraction runs.
    const usePrompt = form.useCustomPrompt;
    return {
      name: form.name.trim(),
      receiver_emails: [...form.receiverEmails],
      date_types: [...form.dateTypes],
      prompt: usePrompt ? form.prompt.trim() : null,
      example_data: usePrompt && exampleData.length > 0 ? exampleData : null,
      first_reminder_weeks: form.firstReminderWeeks,
      second_reminder_weeks: form.secondReminderWeeks,
      // Picking clears the search; an empty search is kept, and matches all.
      contract_criteria:
        form.sourceMode === 'search'
          ? {
              text: form.contractCriteria.text.trim(),
              column_filters: {
                ...(form.contractCriteria.types.length > 0
                  ? { type: [...form.contractCriteria.types] }
                  : {}),
                ...(form.contractCriteria.tags.length > 0
                  ? { tags: [...form.contractCriteria.tags] }
                  : {}),
              },
            }
          : null,
    };
  }

  /**
   * Apply the link diffs and report what the server refused.
   *
   * Every call is attempted even if an earlier one fails: the collections are
   * independent, and stopping at the first failure would leave the calendar in
   * a state nobody asked for. The per-uid `error` strings are what get shown —
   * "2 failed" alone gives the user nothing to act on.
   */
  async function commitLinks(uid: string): Promise<string[]> {
    const previous = pristineLinks.value;
    // Searching clears the picks, so every folder and contract is unlinked.
    const picking = form.sourceMode === 'pick';
    const plans = [
      {
        label: 'receivers',
        diff: diffUids(previous.receiverUids, form.receiverUids),
        add: (uids: string[]): Promise<ContractManagementCalendarLinkResult> =>
          CalendarService.addReceivers(uid, uids),
        remove: (uids: string[]): Promise<ContractManagementCalendarLinkResult> =>
          CalendarService.removeReceivers(uid, uids),
      },
      {
        label: 'folders',
        diff: diffUids(previous.sourceDirectoryUids, picking ? form.sourceDirectoryUids : []),
        add: (uids: string[]): Promise<ContractManagementCalendarLinkResult> =>
          CalendarService.addSourceDirectories(uid, uids),
        remove: (uids: string[]): Promise<ContractManagementCalendarLinkResult> =>
          CalendarService.removeSourceDirectories(uid, uids),
      },
      {
        label: 'contracts',
        diff: diffUids(previous.sourceContractUids, picking ? form.sourceContractUids : []),
        add: (uids: string[]): Promise<ContractManagementCalendarLinkResult> =>
          CalendarService.addSourceContracts(uid, uids),
        remove: (uids: string[]): Promise<ContractManagementCalendarLinkResult> =>
          CalendarService.removeSourceContracts(uid, uids),
      },
    ];

    const calls: {
      label: string;
      verb: string;
      run: () => Promise<ContractManagementCalendarLinkResult>;
    }[] = [];
    for (const plan of plans) {
      if (plan.diff.added.length > 0) {
        calls.push({ label: plan.label, verb: 'link', run: () => plan.add(plan.diff.added) });
      }
      if (plan.diff.removed.length > 0) {
        calls.push({
          label: plan.label,
          verb: 'unlink',
          run: () => plan.remove(plan.diff.removed),
        });
      }
    }

    const outcomes = await Promise.allSettled(calls.map(call => call.run()));
    const problems: string[] = [];

    for (const [index, outcome] of outcomes.entries()) {
      const { label, verb } = calls[index];
      if (outcome.status === 'rejected') {
        logger.error(`Error trying to ${verb} calendar ${label}:`, outcome.reason);
        problems.push(`Could not ${verb} ${label}.`);
        continue;
      }
      const failures = outcome.value.results.filter(item => !item.success);
      for (const failure of failures) {
        problems.push(`Could not ${verb} one of the ${label}: ${failure.error ?? 'unknown error'}`);
      }
    }

    return problems;
  }

  async function save(): Promise<void> {
    if (saving.value) return;

    saving.value = true;
    saveError.value = null;
    try {
      const payload = buildPayload();
      const uid = calendarUid.value;
      const saved =
        uid === null
          ? await CalendarService.create(payload)
          : await CalendarService.update(uid, payload);

      const problems = await commitLinks(saved.uid);
      if (problems.length > 0) {
        // The calendar itself saved, so this is a warning, not an error — but
        // it names what went wrong rather than just counting it.
        message.warning(
          `${uid === null ? 'Calendar created' : 'Calendar updated'}, but some links did not apply. ${problems.join(' ')}`
        );
      } else {
        message.success(uid === null ? 'Calendar created' : 'Calendar updated');
      }

      void router.push({
        name: 'contract-management-calendar-detail',
        params: { uid: saved.uid },
      });
    } catch (error) {
      logger.error('Error saving calendar:', error);
      saveError.value =
        error instanceof Error
          ? error.message
          : `Failed to ${calendarUid.value === null ? 'create' : 'update'} calendar`;
    } finally {
      saving.value = false;
    }
  }

  async function goNext(): Promise<void> {
    if (continueDisabled.value) return;
    if (isLastStep.value) {
      await save();
      return;
    }
    activeStep.value = CALENDAR_WIZARD_STEPS[currentStepIndex.value + 1].key;
  }

  return {
    form,
    seeds,
    activeStep,
    activeStepDefinition,
    isEditMode,
    title,
    loading,
    loadError,
    saving,
    saveError,
    activeStepBlocker,
    continueDisabled,
    canGoBack,
    continueLabel,
    footerNote,
    isStepComplete,
    isStepAccessible,
    selectStep,
    goBack,
    goNext,
    updateForm,
    hydrate,
    navigateBack,
  };
}
